import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, ShieldCheck, Sparkles, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function Billing() {
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/subscription/plans`),
      axios.get(`${API}/subscription/current`, { withCredentials: true }),
    ]).then(([p, c]) => {
      setPlans(p.data.plans || []);
      setCurrent(c.data);
    }).catch(() => toast.error('Could not load billing'))
      .finally(() => setLoading(false));
  }, []);

  const subscribe = async (planId) => {
    setPaying(planId);
    try {
      const r = await axios.post(`${API}/subscription/initialize`, { plan_id: planId, callback_path: '/billing/callback' }, { withCredentials: true });
      if (r.data?.authorization_url) {
        window.location.href = r.data.authorization_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start payment');
    } finally { setPaying(null); }
  };

  if (loading) return <div className="p-10 text-center text-[var(--text-muted)]">Loading billing…</div>;

  const currentPlanId = current?.plan_id;
  const isAdminFree = current?.is_admin_free;
  const isTrial = current?.is_trial;
  const trialEnds = current?.trial_ends_at;

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10">
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-extrabold text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Billing & Plan</h1>
        <p className="text-[var(--text-secondary)] mt-1">Manage your SAIL Books subscription.</p>
      </header>

      {/* Current plan card */}
      <div className={`rounded-2xl p-6 md:p-8 mb-8 border ${isAdminFree ? 'bg-gradient-to-br from-[var(--ocean-coral)]/15 to-[var(--ocean-surface)]/10 border-[var(--ocean-coral)]/40' : 'bg-gradient-to-br from-[var(--ocean-deep)] to-[var(--ocean-surface)] text-white border-transparent'}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className={`text-xs uppercase tracking-wider font-bold ${isAdminFree ? 'text-[var(--ocean-coral)]' : 'text-white/70'}`}>Current plan</p>
            <h2 className={`text-3xl font-extrabold mt-1 ${isAdminFree ? 'text-[var(--ocean-deep)]' : 'text-white'} flex items-center gap-2`} style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
              {isAdminFree && <Crown className="w-7 h-7 text-[var(--ocean-coral)]"/>}
              {current?.plan?.name || 'Free'}
            </h2>
            {isTrial && trialEnds && (
              <p className="text-sm mt-2 text-white/85 flex items-center gap-1.5"><Sparkles className="w-4 h-4"/> Trial ends {new Date(trialEnds).toLocaleDateString()}</p>
            )}
            {isAdminFree && <p className="text-sm text-[var(--text-secondary)] mt-2">Your account is on the internal SAIL admin plan — full access, no billing.</p>}
            {current?.next_billing_date && !isTrial && !isAdminFree && (
              <p className="text-sm mt-2 text-white/85">Next billing date: {new Date(current.next_billing_date).toLocaleDateString()}</p>
            )}
          </div>
          <div className={`px-4 py-2 rounded-xl text-xs font-bold ${isAdminFree ? 'bg-[var(--ocean-coral)]/20 text-[var(--ocean-coral)]' : 'bg-white/15 text-white'}`}>
            Status: {(current?.status || 'active').toUpperCase()}
          </div>
        </div>
      </div>

      {!isAdminFree && (
        <>
          <h3 className="text-xl font-bold text-[var(--ocean-deep)] mb-4" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Change plan</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map(p => {
              const isCurrent = p.id === currentPlanId;
              const hl = p.id === 'professional';
              return (
                <div key={p.id} className={`relative rounded-2xl p-6 border flex flex-col ${hl ? 'bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-mid)] text-white border-transparent' : 'bg-white border-[var(--border-glass)]'}`}>
                  {hl && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-[var(--ocean-coral)] text-white text-[10px] font-bold uppercase tracking-wider">Popular</div>}
                  <p className={`text-xs font-bold uppercase tracking-wider ${hl ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>{p.name}</p>
                  <p className={`text-3xl font-extrabold mt-2 ${hl ? 'text-white' : 'text-[var(--ocean-deep)]'}`} style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>R{p.price.toLocaleString()}<span className={`text-sm font-medium ${hl ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>/user/mo</span></p>
                  <ul className="mt-4 space-y-2 text-sm flex-1">
                    {(p.features || []).slice(0, 4).map(f => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 ${hl ? 'text-[var(--ocean-shallow)]' : 'text-[var(--ocean-surface)]'}`} />
                        <span className={hl ? 'text-white/90' : 'text-[var(--text-secondary)]'}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <button disabled className={`mt-5 px-4 py-3 rounded-xl font-semibold ${hl ? 'bg-white/20 text-white' : 'bg-[var(--ocean-foam)] text-[var(--ocean-deep)]'}`}>Current plan</button>
                  ) : p.id === 'free' ? (
                    <button disabled className="mt-5 px-4 py-3 rounded-xl font-semibold bg-[var(--ocean-foam)] text-[var(--text-muted)] cursor-not-allowed">Downgrade — contact us</button>
                  ) : (
                    <button onClick={() => subscribe(p.id)} disabled={paying === p.id} className={`mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 ${hl ? 'bg-white text-[var(--ocean-deep)] hover:bg-[var(--ocean-foam)]' : 'bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-mid)] text-white hover:-translate-y-0.5'}`}>
                      {paying === p.id ? 'Loading…' : 'Upgrade'} <ArrowRight className="w-4 h-4"/>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-8 p-5 rounded-2xl bg-white/70 border border-[var(--border-glass)] flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[var(--ocean-surface)] flex-shrink-0" />
            <p className="text-sm text-[var(--text-secondary)]">Payments are processed securely by <strong>Paystack</strong>. We never store your card details.</p>
          </div>
        </>
      )}
    </div>
  );
}
